import dayjs from 'dayjs';
import { IAdminMainMetricsData, IAdminMainMetricsQuery } from '@/models/dashboard.model';

const emptyData = (from: string | null, to: string | null): IAdminMainMetricsData => ({
  period: { from, to },
  previous_period: null,
  records: {
    max_active_orders_per_day: { value: 0, date: '' },
    max_active_orders_amount_per_day: { value: 0, date: '' },
  },
  kpis: {
    active_orders_count: { value: 0, change_percent: null },
    active_orders_amount: { value: 0, change_percent: null },
    avg_check: { value: 0, change_percent: null },
    cancelled: { count: 0, share_percent: 0, amount: 0 },
    new_users: { value: 0, change_percent: null },
    purchase_rate_pr: { value: 0, change_pp: null },
    customer_ltv: { value: 0, change_percent: null },
  },
  active_orders_series: [],
  paid: {
    amount: { value: 0, change_percent: null },
    count: { value: 0, change_percent: null },
    series: [],
    payment_types: [],
  },
  stock: {
    as_of: '',
    total_amount: 0,
    growth_amount: 0,
    series: [],
    by_region: [],
    top_suppliers: [],
  },
  catalog: { sku_total: 0, published: 0, hidden: 0, deleted: 0, series: [] },
  suppliers: { total: { value: 0, change_percent: null }, growth: 0, departed: 0, series: [] },
  engagement: { percent: 0, change_pp: null, avg_active_shops_per_day: 0, daily: [] },
  top_suppliers: [],
  top_products: [],
});

export const getMockAdminMainMetricsData = (query: IAdminMainMetricsQuery = {}): IAdminMainMetricsData => {
  const today = dayjs().startOf('day');
  const rangeFrom = query.date_from ? dayjs(query.date_from).startOf('day') : today.startOf('month');
  let rangeTo = query.date_to ? dayjs(query.date_to).startOf('day') : today;

  if (rangeTo.isAfter(today)) rangeTo = today;
  if (rangeFrom.isAfter(rangeTo)) {
    return emptyData(query.date_from ?? null, query.date_to ?? null);
  }

  const days = rangeTo.diff(rangeFrom, 'day') + 1;
  const activeOrdersSeries = Array.from({ length: days }, (_, index) => {
    const date = rangeFrom.add(index, 'day');
    const count = 28 + (index % 9);
    const amount = count * (2800 + (index % 7) * 120);
    return {
      date: date.format('YYYY-MM-DD'),
      amount,
      count,
      prev_amount: Number((amount * 0.9).toFixed(2)),
      prev_count: Math.max(1, count - 3),
    };
  });

  const activeCount = activeOrdersSeries.reduce((sum, item) => sum + item.count, 0);
  const activeAmount = activeOrdersSeries.reduce((sum, item) => sum + item.amount, 0);
  const paidSeries = activeOrdersSeries.map((item) => ({
    ...item,
    count: Math.max(1, Math.round(item.count * 0.85)),
    amount: Number((item.amount * 0.86).toFixed(2)),
    prev_amount: Number(((item.prev_amount ?? 0) * 0.86).toFixed(2)),
    prev_count: Math.max(1, Math.round((item.prev_count ?? 1) * 0.85)),
  }));
  const paidCount = paidSeries.reduce((sum, item) => sum + item.count, 0);
  const paidAmount = paidSeries.reduce((sum, item) => sum + item.amount, 0);

  const stockSeries = activeOrdersSeries.map((item, index) => ({
    date: item.date,
    amount: 18000000 + index * 42000,
    prev_amount: 17500000 + index * 38000,
  }));

  const catalogSeries = activeOrdersSeries.map((item, index) => ({
    date: item.date,
    total: 48000 + index * 12,
    published: 40800 + index * 10,
    hidden: 7200 + index * 2,
    deleted: 180 + index,
  }));

  const suppliersSeries = activeOrdersSeries.map((item, index) => ({
    date: item.date,
    total: 230 + Math.floor(index / 3),
    prev_total: 220 + Math.floor(index / 3),
  }));

  const engagementDaily = activeOrdersSeries.map((item, index) => ({
    date: item.date,
    percent: Number((42 + (index % 10) * 1.4).toFixed(1)),
  }));

  return {
    period: {
      from: rangeFrom.format('YYYY-MM-DD'),
      to: rangeTo.format('YYYY-MM-DD'),
    },
    previous_period: {
      from: rangeFrom.subtract(days, 'day').format('YYYY-MM-DD'),
      to: rangeFrom.subtract(1, 'day').format('YYYY-MM-DD'),
    },
    records: {
      max_active_orders_per_day: { value: 64, date: '2024-11-12' },
      max_active_orders_amount_per_day: { value: 248600, date: '2025-03-08' },
    },
    kpis: {
      active_orders_count: { value: activeCount, change_percent: 8 },
      active_orders_amount: { value: activeAmount, change_percent: 12.8 },
      avg_check: {
        value: Number((activeAmount / Math.max(activeCount, 1)).toFixed(2)),
        change_percent: 4.4,
      },
      cancelled: {
        count: Math.max(1, Math.round(activeCount * 0.03)),
        share_percent: 3.1,
        amount: Math.round(activeAmount * 0.028),
      },
      new_users: { value: 1840, change_percent: 9.2 },
      purchase_rate_pr: { value: 28.4, change_pp: 1.2 },
      customer_ltv: { value: 8420, change_percent: 3.6 },
    },
    active_orders_series: activeOrdersSeries,
    paid: {
      amount: { value: paidAmount, change_percent: 14.2 },
      count: { value: paidCount, change_percent: 11.5 },
      series: paidSeries,
      payment_types: [
        { code: 'cash', label: 'Готівка', amount: paidAmount * 0.22, share_percent: 22 },
        { code: 'acquiring', label: 'Інтернет-еквайринг', amount: paidAmount * 0.34, share_percent: 34 },
        { code: 'bank', label: 'Оплата на р/р', amount: paidAmount * 0.18, share_percent: 18 },
        { code: 'terminal', label: 'Банківський термінал', amount: paidAmount * 0.12, share_percent: 12 },
        { code: 'novapay', label: 'NovaPay', amount: paidAmount * 0.14, share_percent: 14 },
      ],
    },
    stock: {
      as_of: rangeTo.format('YYYY-MM-DD'),
      total_amount: 18640000,
      growth_amount: 2140000,
      series: stockSeries,
      by_region: [
        { id: 'poltava', name: 'Полтава', amount: 7456000, share_percent: 40 },
        { id: 'kyiv', name: 'Київ', amount: 6524000, share_percent: 35 },
        { id: 'kharkiv', name: 'Харків', amount: 4660000, share_percent: 25 },
      ],
      top_suppliers: Array.from({ length: 10 }, (_, index) => ({
        rank: index + 1,
        id: `s${index + 1}`,
        name: `Постачальник ${index + 1}`,
        amount: 1200000 - index * 85000,
        share_percent: Number((12 - index * 0.7).toFixed(1)),
        change_percent: Number((8 - index * 1.2).toFixed(1)),
      })),
    },
    catalog: {
      sku_total: 48620,
      published: 41327,
      hidden: 6123,
      deleted: 1170,
      series: catalogSeries,
    },
    suppliers: {
      total: { value: 248, change_percent: 7.8 },
      growth: 18,
      departed: 4,
      series: suppliersSeries,
    },
    engagement: {
      percent: 46.2,
      change_pp: 2.1,
      avg_active_shops_per_day: 114,
      daily: engagementDaily,
    },
    top_suppliers: Array.from({ length: 100 }, (_, index) => ({
      rank: index + 1,
      id: `ts${index + 1}`,
      name: `Постачальник ${index + 1}`,
      amount: 980000 - index * 6200,
      orders_count: 420 - index * 2,
      avg_check: 3200 - index * 4,
      change_percent: Number((15 - index * 0.2).toFixed(1)),
    })),
    top_products: Array.from({ length: 100 }, (_, index) => ({
      rank: index + 1,
      id: `tp${index + 1}`,
      name: `Товар ${index + 1}`,
      sku: `SKU-${1000 + index}`,
      supplier_name: `Постачальник ${(index % 20) + 1}`,
      amount: 540000 - index * 3800,
      orders_count: 260 - index,
      avg_check: 2800 - index * 3,
      change_percent: Number((12 - index * 0.15).toFixed(1)),
    })),
  };
};
